import { useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Twitter, Instagram, Mail, CheckCircle2, ArrowRight } from "lucide-react";
import api from "@/lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try { await api.post("/newsletter", { email }); setDone(true); setEmail(""); } catch (e) { setDone(true); }
  };

  return (
    <footer className="bg-ink-950 text-neutral-300 mt-20">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-5 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center mb-4">
              <img src="/gkdeals-logo-sm.webp" alt="gkdeals.com" width="71" height="40" className="h-10 w-auto" />
            </div>
            <p className="text-sm max-w-sm leading-relaxed text-neutral-400">
              Your go-to destination for hand-picked deals, verified coupons and honest product reviews —
              automated and updated daily to save you time and money.
            </p>
            <div className="flex gap-3 mt-6">
              {[Facebook, Twitter, Instagram].map((Icon, i) => (
                <a key={i} href="#" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-brand transition-colors" data-testid={`footer-social-${i}`}>
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-white font-display font-bold mb-4 text-sm uppercase tracking-widest">Explore</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/deals" className="hover:text-brand transition-colors">Top Deals</Link></li>
              <li><Link to="/travel" className="hover:text-brand transition-colors">Travel Deals</Link></li>
              <li><Link to="/health" className="hover:text-brand transition-colors">Health Deals</Link></li>
              <li><Link to="/coupons" className="hover:text-brand transition-colors">Coupons</Link></li>
              <li><Link to="/stores" className="hover:text-brand transition-colors" data-testid="footer-stores-link">All Stores A–Z</Link></li>
              <li><Link to="/reviews" className="hover:text-brand transition-colors">Product Reviews</Link></li>
              <li><Link to="/guides" className="hover:text-brand transition-colors">How-To Guides</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-display font-bold mb-4 text-sm uppercase tracking-widest">Company</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/about" className="hover:text-brand transition-colors" data-testid="footer-about">About Us</Link></li>
              <li><Link to="/author/gordon-kokoma" className="hover:text-brand transition-colors" data-testid="footer-editor">Meet Our Editor</Link></li>
              <li><Link to="/contact" className="hover:text-brand transition-colors" data-testid="footer-contact">Contact</Link></li>
              <li><Link to="/affiliate-disclosure" className="hover:text-brand transition-colors" data-testid="footer-affiliate">Affiliate Disclosure</Link></li>
              <li><Link to="/privacy" className="hover:text-brand transition-colors" data-testid="footer-privacy">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-brand transition-colors" data-testid="footer-terms">Terms of Service</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-display font-bold mb-4 text-sm uppercase tracking-widest">Newsletter</h4>
            <p className="text-sm text-neutral-400 mb-4">Get the best deals in your inbox — no spam, ever.</p>
            {done ? (
              <div className="flex items-center gap-2 text-sm font-semibold text-green-400 bg-green-400/10 border border-green-400/20 rounded-2xl px-4 py-3.5" data-testid="newsletter-success">
                <CheckCircle2 className="w-5 h-5 shrink-0" /> You're subscribed! Watch your inbox.
              </div>
            ) : (
              <form onSubmit={subscribe} className="space-y-3" data-testid="newsletter-form">
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500 pointer-events-none" />
                  <input data-testid="newsletter-email-input" value={email} onChange={(e) => setEmail(e.target.value)} type="email" required
                    placeholder="you@email.com"
                    className="w-full pl-11 pr-4 h-12 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder:text-neutral-500 focus:outline-none focus:bg-white/10 focus:border-brand/50 focus-visible:ring-2 focus-visible:ring-brand/40 transition-colors" />
                </div>
                <button data-testid="newsletter-submit"
                  className="group w-full h-12 rounded-xl bg-brand hover:bg-brand-hover font-semibold text-white flex items-center justify-center gap-2 transition-colors">
                  Subscribe
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </button>
                <p className="text-[11px] text-neutral-500">Join 10,000+ shoppers. Unsubscribe anytime.</p>
              </form>
            )}
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-6 flex flex-col sm:flex-row justify-between gap-3 text-xs text-neutral-500">
          <p data-testid="footer-copyright">© {new Date().getFullYear()} gkdeals.com, all rights reserved. As an affiliate we may earn commissions from qualifying purchases.</p>
          <p>Deals, coupons & reviews — automated for you.</p>
        </div>
      </div>
    </footer>
  );
}
